import React, { useState } from 'react';
import { Send, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

const SUBJECTS = [
  { value: '', label: 'Select a subject...' },
  { value: 'general', label: 'General Inquiry' },
  { value: 'technical', label: 'Technical Support' },
  { value: 'billing', label: 'Billing' },
  { value: 'feedback', label: 'Feedback' },
];

const ContactForm = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const validate = () => {
    const errs = {};
    if (!form?.name?.trim()) errs.name = 'Full name is required.';
    if (!form?.email?.trim()) {
      errs.email = 'Email address is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(form?.email)) {
      errs.email = 'Please enter a valid email address.';
    }
    if (!form?.subject) errs.subject = 'Please select a subject.';
    if (!form?.message?.trim()) {
      errs.message = 'Message is required.';
    } else if (form?.message?.trim()?.length < 10) {
      errs.message = 'Message must be at least 10 characters.';
    }
    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const errs = validate();
    if (Object.keys(errs)?.length > 0) { setErrors(errs); return; }

    setStatus('loading');
    setErrorMsg('');

    try {
      const { data, error } = await supabase?.functions?.invoke('send-contact-email', {
        body: {
          name: form?.name?.trim(),
          email: form?.email?.trim(),
          subject: form?.subject,
          message: form?.message?.trim(),
        }
      });

      if (error) throw new Error(error.message || 'Failed to send message.');
      if (data?.error) throw new Error(data.error);

      setStatus('success');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      setStatus('error');
      setErrorMsg(err?.message || 'Something went wrong. Please try again or email us directly.');
    }
  };

  const inputBase = {
    width: '100%',
    padding: '12px 14px',
    borderRadius: '10px',
    border: '1.5px solid rgba(27,58,92,0.18)',
    background: '#ffffff',
    color: '#0F2035',
    fontSize: '15px',
    fontFamily: "'DM Sans', sans-serif",
    outline: 'none',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#1B3A5C',
    marginBottom: '6px',
    letterSpacing: '0.01em',
  };

  if (status === 'success') {
    return (
      <div
        className="flex flex-col items-center justify-center text-center py-14 px-6"
        style={{ background: '#ffffff', borderRadius: '20px', border: '1px solid rgba(27,58,92,0.1)', boxShadow: '0 4px 24px rgba(27,58,92,0.07)' }}
      >
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center mb-5"
          style={{ background: 'rgba(26,122,74,0.1)' }}
        >
          <CheckCircle size={32} style={{ color: '#1A7A4A' }} />
        </div>
        <h3 className="text-xl font-bold mb-2" style={{ color: '#0D2137' }}>Message Sent!</h3>
        <p className="text-sm mb-6" style={{ color: '#4A6080', maxWidth: '320px', lineHeight: '1.6' }}>
          Thanks for reaching out. Our support team typically responds within 1 business day.
        </p>
        <button
          onClick={() => setStatus('idle')}
          className="px-6 py-2.5 rounded-xl text-sm font-semibold transition-all hover:opacity-90"
          style={{ background: 'linear-gradient(135deg, #E8732A, #f08040)', color: '#ffffff' }}
        >
          Send Another Message
        </button>
      </div>
    );
  }

  return (
    <div
      style={{ background: '#ffffff', borderRadius: '20px', border: '1px solid rgba(27,58,92,0.1)', boxShadow: '0 4px 24px rgba(27,58,92,0.07)', padding: '36px' }}
    >
      <h2 className="text-xl font-bold mb-1" style={{ color: '#0D2137' }}>Send Us a Message</h2>
      <p className="text-sm mb-7" style={{ color: '#4A6080' }}>Fill out the form below and we'll get back to you promptly.</p>
      {status === 'error' && (
        <div
          className="flex items-start gap-3 p-4 rounded-xl mb-6"
          style={{ background: 'rgba(198,40,40,0.07)', border: '1px solid rgba(198,40,40,0.2)' }}
        >
          <AlertCircle size={18} style={{ color: '#C62828', flexShrink: 0, marginTop: '1px' }} />
          <p className="text-sm" style={{ color: '#C62828' }}>{errorMsg}</p>
        </div>
      )}
      <form onSubmit={handleSubmit} noValidate>
        <div className="grid sm:grid-cols-2 gap-5 mb-5">
          {/* Full Name */}
          <div>
            <label style={labelStyle}>Full Name <span style={{ color: '#E8732A' }}>*</span></label>
            <input
              type="text"
              name="name"
              value={form?.name}
              onChange={handleChange}
              placeholder="Jane Smith"
              style={{ ...inputBase, borderColor: errors?.name ? '#C62828' : 'rgba(27,58,92,0.18)' }}
              onFocus={e => { e.target.style.borderColor = '#E8732A'; e.target.style.boxShadow = '0 0 0 3px rgba(232,115,42,0.12)'; }}
              onBlur={e => { e.target.style.borderColor = errors?.name ? '#C62828' : 'rgba(27,58,92,0.18)'; e.target.style.boxShadow = 'none'; }}
            />
            {errors?.name && <p className="text-xs mt-1.5" style={{ color: '#C62828' }}>{errors?.name}</p>}
          </div>
          {/* Email */}
          <div>
            <label style={labelStyle}>Email Address <span style={{ color: '#E8732A' }}>*</span></label>
            <input
              type="email"
              name="email"
              value={form?.email}
              onChange={handleChange}
              placeholder="jane@company.com"
              style={{ ...inputBase, borderColor: errors?.email ? '#C62828' : 'rgba(27,58,92,0.18)' }}
              onFocus={e => { e.target.style.borderColor = '#E8732A'; e.target.style.boxShadow = '0 0 0 3px rgba(232,115,42,0.12)'; }}
              onBlur={e => { e.target.style.borderColor = errors?.email ? '#C62828' : 'rgba(27,58,92,0.18)'; e.target.style.boxShadow = 'none'; }}
            />
            {errors?.email && <p className="text-xs mt-1.5" style={{ color: '#C62828' }}>{errors?.email}</p>}
          </div>
        </div>

        {/* Subject */}
        <div className="mb-5">
          <label style={labelStyle}>Subject <span style={{ color: '#E8732A' }}>*</span></label>
          <select
            name="subject"
            value={form?.subject}
            onChange={handleChange}
            style={{ ...inputBase, borderColor: errors?.subject ? '#C62828' : 'rgba(27,58,92,0.18)', cursor: 'pointer', appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%234A6080' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center' }}
            onFocus={e => { e.target.style.borderColor = '#E8732A'; e.target.style.boxShadow = '0 0 0 3px rgba(232,115,42,0.12)'; }}
            onBlur={e => { e.target.style.borderColor = errors?.subject ? '#C62828' : 'rgba(27,58,92,0.18)'; e.target.style.boxShadow = 'none'; }}
          >
            {SUBJECTS?.map(s => (
              <option key={s?.value} value={s?.value} disabled={s?.value === ''}>{s?.label}</option>
            ))}
          </select>
          {errors?.subject && <p className="text-xs mt-1.5" style={{ color: '#C62828' }}>{errors?.subject}</p>}
        </div>

        {/* Message */}
        <div className="mb-7">
          <label style={labelStyle}>Message <span style={{ color: '#E8732A' }}>*</span></label>
          <textarea
            name="message"
            value={form?.message}
            onChange={handleChange}
            rows={5}
            placeholder="Describe your question or issue in detail..."
            style={{ ...inputBase, resize: 'vertical', minHeight: '130px', borderColor: errors?.message ? '#C62828' : 'rgba(27,58,92,0.18)' }}
            onFocus={e => { e.target.style.borderColor = '#E8732A'; e.target.style.boxShadow = '0 0 0 3px rgba(232,115,42,0.12)'; }}
            onBlur={e => { e.target.style.borderColor = errors?.message ? '#C62828' : 'rgba(27,58,92,0.18)'; e.target.style.boxShadow = 'none'; }}
          />
          {errors?.message && <p className="text-xs mt-1.5" style={{ color: '#C62828' }}>{errors?.message}</p>}
          <p className="text-xs mt-1.5" style={{ color: '#4A6080' }}>{form?.message?.length} characters</p>
        </div>

        <button
          type="submit"
          disabled={status === 'loading'}
          className="w-full flex items-center justify-center gap-2.5 py-3.5 rounded-xl font-semibold text-sm transition-all hover:opacity-90 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ background: 'linear-gradient(135deg, #E8732A, #f08040)', color: '#ffffff', boxShadow: '0 4px 16px rgba(232,115,42,0.3)' }}
        >
          {status === 'loading' ? (
            <><Loader size={16} className="animate-spin" /> Sending...</>
          ) : (
            <><Send size={16} /> Send Message</>
          )}
        </button>
      </form>
    </div>
  );
};

export default ContactForm;
